import { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Spin, Table, Tag } from '@arco-design/web-react'
import {
  getMonitorMetricsSummaryApi,
  getMonitorStatusApi,
  listAlertEventsApi,
  type AlertEventItem,
  type MonitorMetricsSummary,
  type MonitorStatus,
} from '@/api/monitor'

function healthTag(ok: boolean, label: string) {
  return <Tag color={ok ? 'green' : 'red'}>{label}</Tag>
}

export default function MonitorStatusCard() {
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState<MonitorStatus | null>(null)
  const [summary, setSummary] = useState<MonitorMetricsSummary | null>(null)
  const [events, setEvents] = useState<AlertEventItem[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [s, m, e] = await Promise.all([
        getMonitorStatusApi(),
        getMonitorMetricsSummaryApi(),
        listAlertEventsApi(),
      ])
      setStatus(s)
      setSummary(m)
      setEvents(e ?? [])
    } catch {
      setStatus(null)
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  if (loading && !status) {
    return (
      <div className="settings-section mb-3 p-6 flex justify-center">
        <Spin />
      </div>
    )
  }

  return (
    <div className="settings-section mb-3">
      <div className="settings-section-heading mb-3">
        <div>
          <div className="settings-section-title">当前监控状态</div>
          <div className="settings-section-subtitle">近 5 分钟请求与系统健康（Root 可见）</div>
        </div>
        <Button size="small" onClick={load} loading={loading}>
          刷新
        </Button>
      </div>

      {!status ? (
        <Alert type="error" content="无法加载监控状态，请确认当前账号为 Root。" />
      ) : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="p-3 rounded border border-[var(--color-border-2)]">
              <div className="text-xs text-[var(--color-text-3)] mb-1">依赖健康</div>
              <div className="flex flex-wrap gap-1">
                {healthTag(status.backend_healthy, '后端')}
                {healthTag(status.mysql_healthy, 'MySQL')}
                {status.redis_enabled ? healthTag(status.redis_healthy, 'Redis') : <Tag>Redis 未启用</Tag>}
              </div>
            </div>
            <div className="p-3 rounded border border-[var(--color-border-2)]">
              <div className="text-xs text-[var(--color-text-3)] mb-1">近 5 分钟</div>
              <div>请求 {status.requests_5m} · 错误率 {status.error_rate_5m.toFixed(1)}%</div>
              <div>慢请求 {status.slow_requests_5m}</div>
            </div>
            <div className="p-3 rounded border border-[var(--color-border-2)]">
              <div className="text-xs text-[var(--color-text-3)] mb-1">系统资源</div>
              <div>CPU {status.cpu_usage_percent.toFixed(1)}%</div>
              <div>内存 {status.memory_usage_percent.toFixed(1)}% · 磁盘 {status.disk_usage_percent.toFixed(1)}%</div>
            </div>
            {summary && (
              <div className="p-3 rounded border border-[var(--color-border-2)]">
                <div className="text-xs text-[var(--color-text-3)] mb-1">Relay 摘要</div>
                <div>RPM {summary.rpm.toFixed(1)} · TPM {summary.tpm.toFixed(0)}</div>
                <div>P95 {summary.p95_total_ms}ms · TTFT {summary.avg_ttft_ms.toFixed(0)}ms</div>
              </div>
            )}
          </div>

          <div className="text-sm font-medium mb-2">最近告警事件</div>
          <Table
            size="small"
            pagination={false}
            data={events.slice(0, 10)}
            columns={[
              { title: '时间', dataIndex: 'created_at', width: 160, render: (v: number) => (v ? new Date(v * 1000).toLocaleString() : '-') },
              { title: '类型', dataIndex: 'event_type', width: 180 },
              { title: '级别', dataIndex: 'severity', width: 80 },
              { title: 'request_id', dataIndex: 'request_id', ellipsis: true },
              { title: 'Webhook', dataIndex: 'webhook_status', width: 90 },
            ]}
            noDataElement="暂无告警事件"
          />
        </>
      )}
    </div>
  )
}
